/**
 * GUPM-302 — rede de segurança para chunk que não existe mais.
 *
 * Medido contra a produção em 21/08: um asset inexistente responde `HTTP 200` com
 * `content-type: text/html` — é o fallback de SPA (`/* /index.html 200` em
 * `public/_redirects`) do Cloudflare Pages. O `import()` recebe HTML em vez de
 * JavaScript e estoura. Como o fallback dos dois `lazy()` do app é
 * `<Suspense fallback={null}>` e **não existia nenhum ErrorBoundary em `src/`**, a
 * árvore React desmontava inteira: tela branca.
 *
 * Este componente é a segunda camada. O `useNovaVersao` avisa ANTES da falha; este
 * segura SE a falha vier primeiro (aba muito antiga, ou usuário que clicou rápido).
 *
 * Classe porque `componentDidCatch` é o único jeito no React 18 — não existe hook
 * equivalente. Fica em `components/` e não em `features/` porque serve a qualquer
 * `lazy()` futuro.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { eFalhaDeChunk } from '@/domain/versao/deploy';
import { ROTULO_RECARREGAR, TITULO_NOVA_VERSAO } from '@/components/versao-textos';
import { usePopup } from '@/hooks/usePopup';

/**
 * `aoFechar` é o que distingue os dois usos e é opcional de propósito:
 *   - **informado** — o boundary está no lugar de um MODAL. Aí ele aparece como overlay
 *     (senão viraria um card no rodapé da página, abaixo da lista inteira do MPA e fora
 *     do viewport) e oferece "Fechar", para o dono do modal poder voltar ao estado
 *     anterior. Sem isso o `modalOpen` fica `true` para sempre — o `onClose` do modal
 *     nunca chegou a montar — e o botão que abre o modal vira um no-op.
 *   - **ausente** — o boundary está no lugar de um pedaço DENTRO de uma tela (o
 *     `MapPicker`, dentro do próprio `NovoPlanoModal`). Fica no fluxo, sem cobrir o modal
 *     que o contém, e quem fecha é o modal.
 */
type Props = { children: ReactNode; aoFechar?: () => void };

export class ChunkErrorBoundary extends Component<Props, { falhou: boolean; eChunk: boolean }> {
  state = { falhou: false, eChunk: false };

  static getDerivedStateFromError(error: unknown) {
    // Guardar a CLASSIFICAÇÃO, não o erro: o que a tela precisa é só decidir o texto, e
    // um `Error` no state seria dado a mais vivendo mais tempo do que precisa.
    return { falhou: true, eChunk: eFalhaDeChunk(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Observabilidade: a falha não pode passar calada, mas também não pode vazar dado
    // de usuário. Só a mensagem e o componente — sem props, sem sessão.
    console.error('[chunk] erro capturado no boundary', {
      eFalhaDeChunk: eFalhaDeChunk(error),
      message: error.message,
      componentStack: info.componentStack,
    });
  }

  private conteudo() {
    const { aoFechar } = this.props;
    return (
      <div
        role="alert"
        className="rounded-xl border border-level-atencao/40 bg-level-atencao/10 p-4 text-center"
      >
        {this.state.eChunk ? (
          <>
            {/* Título e botão vêm da MESMA constante que o `AvisoNovaVersao` usa: é o
                mesmo assunto, e ter dois nomes para a mesma coisa ("publicada" ×
                "disponível") custa reconhecimento a quem ignorou o banner e bateu aqui
                depois. O corpo é que muda — aqui a pessoa já topou com o problema e
                recarregar não é opcional. E não repetimos a promessa de que nada se
                perde: este boundary envolve justamente o `NovoPlanoModal`, que não
                persiste nada. */}
            <p className="text-sm font-bold text-white">{TITULO_NOVA_VERSAO}</p>
            <p className="mt-1 text-xs leading-snug text-jf-muted">
              Esta tela mudou na nova versão e não foi carregada nesta aba.
              Recarregue a página para abri-la.
            </p>
          </>
        ) : (
          <>
            {/* Não é chunk: não afirmar deploy. Recarregar segue sendo o que a pessoa
                pode tentar, mas o texto não promete que resolve — se o erro for de
                render, vai estourar de novo, e prometer o contrário queima o aviso de
                versão de verdade. */}
            <p className="text-sm font-bold text-white">Não foi possível abrir esta tela</p>
            <p className="mt-1 text-xs leading-snug text-jf-muted">
              Algo deu errado ao carregar esta parte do app. Tente recarregar a página; se
              acontecer de novo, avise o suporte.
            </p>
          </>
        )}
        <div className="mt-3 flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-lg bg-jf-yellow px-3 py-1.5 text-xs font-black text-jf-bg transition hover:brightness-110"
          >
            {ROTULO_RECARREGAR}
          </button>
          {aoFechar && (
            <button
              type="button"
              onClick={aoFechar}
              className="rounded-lg border border-jf-border px-3 py-1.5 text-xs font-bold text-jf-muted transition hover:text-white"
            >
              Fechar
            </button>
          )}
        </div>
      </div>
    );
  }

  render() {
    if (!this.state.falhou) return this.props.children;
    if (!this.props.aoFechar) return this.conteudo();
    return <OverlayDeFalha aoFechar={this.props.aoFechar}>{this.conteudo()}</OverlayDeFalha>;
  }
}

/** O aviso no lugar do modal que não carregou. Esc = "Fechar" (C-91). */
function OverlayDeFalha({ aoFechar, children }: { aoFechar: () => void; children: ReactNode }) {
  const ref = usePopup<HTMLDivElement>({ aoFechar });
  return (
    // `z-[2000]` = o mesmo do `NovoPlanoModal`, que é o modal que este aviso substitui.
    <div
      data-testid="chunk-error-overlay"
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/60 p-4"
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label="Falha ao abrir a tela"
        tabIndex={-1}
        className="w-full max-w-sm focus:outline-none"
      >
        {children}
      </div>
    </div>
  );
}
